import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { App } from '../../models/appcloud/app.model';

@Injectable({
    providedIn: 'root',
})
export class AppService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private APPS_URL: string = environment.publicApiHost + '/appcloud/apps';

    getByGlobalId(globalAppId: string, additionalHeaders: any = {}): Observable<App[]> {
        this.#spinnerService.addProcess('AppService.getAppsByGlobabId.' + globalAppId, 'Loading app details...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.skipInterceptor,
                ...additionalHeaders,
            }),
            params: new HttpParams().set('globalId', globalAppId),
        };

        return this.#http.get<App[]>(this.APPS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app data from Relationship One. Please try again later.'
                );
                return of<App[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.getAppsByGlobabId.' + globalAppId))
        );
    }

    get(id: number, additionalHeaders: any = {}): Observable<App> {
        this.#spinnerService.addProcess('AppService.get', 'Loading app details...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams(),
        };

        return this.#http.get<App>(this.APPS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app data from Relationship One. Please try again later.'
                );
                return of<App>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.get'))
        );
    }

    update(id: number, app: App, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('AppService.update.' + id, 'Updating your configuration...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<App>(this.APPS_URL + '/' + id, app, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while sending data to Relationship One. Please try again later.'
                );
                return of<App>();
            }),
            finalize(() => this.#spinnerService.completeProcess('AppService.update.' + id))
        );
    }
}
